import React from 'react';

import { ModeSelector } from './ModeSelector';
import { CardSpecificSection } from './TieredPricing/CardSpecificSection';
import { SixTierSection } from './TieredPricing/SixTierSection';
import { ThreeTierSection } from './TieredPricing/ThreeTierSection';

import type {
	PricingConfiguration,
	TieredRateStructure,
} from '../../../../types';

interface TieredSectionProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	handleDiscountRateChange: (
		mode: 'same_for_all' | 'different_by_card_type',
	) => void;
	threeTierRates: any;
	sixTierRates: any;
	sameAsAbove: boolean;
	setSameAsAbove: (value: boolean) => void;
	handleSameAsAboveChange: (checked: boolean) => void;
	updateCardSpecificDiscountRate?: (cardType: string, value: number) => void;
	updateTransactionSpecificFee?: (
		transactionType: string,
		value: number,
	) => void;
}

export const TieredSection: React.FC<TieredSectionProps> = ({
	configuration,
	onUpdate,
	handleDiscountRateChange,
	threeTierRates,
	sixTierRates,
	sameAsAbove,
	setSameAsAbove,
	handleSameAsAboveChange,
	updateCardSpecificDiscountRate,
	updateTransactionSpecificFee,
}) => {
	// Use tiered-specific discount rate mode, falling back to global
	const tieredDiscountRateMode =
		configuration.tiered_discount_rate_mode ||
		configuration.discount_rate_mode ||
		'same_for_all';

	return (
		<>
			{/* Mode Selection - Only for TIERED pricing */}
			<ModeSelector
				selectedMode={tieredDiscountRateMode}
				onModeChange={handleDiscountRateChange}
				groupId="tiered"
			/>
			<div className="space-y-8">
				{/* View selection ONLY depends on tieredDiscountRateMode, NOT on "same as above" checkbox */}
				{tieredDiscountRateMode === 'same_for_all' ? (
					<>
						{/* 3-tier Unified Pricing */}
						<ThreeTierSection
							rates={threeTierRates.visa_mastercard_discover_credit}
							onRateChange={(tier, value) => {
								const updatedCreditRates = {
									qualified_rate: 1.65,
									qualified_per_item: 0.91,
									midqualified_rate: 1.75,
									midqualified_per_item: 0.91,
									nonqualified_rate: 2.25,
									nonqualified_per_item: 0.91,
									...configuration.credit_rates,
									[tier]: value,
								} as TieredRateStructure;

								onUpdate({
									credit_rates: updatedCreditRates,
									// If "Same as above" is checked, also update debit rates
									...(sameAsAbove && { debit_rates: updatedCreditRates }),
								});
							}}
						/>

						{/* 6-tier Section with "Same as above" */}
						<SixTierSection
							creditRates={threeTierRates.visa_mastercard_discover_credit}
							debitRates={
								configuration.debit_rates || {
									qualified_rate: 1.6,
									qualified_per_item: 0.79,
									midqualified_rate: 1.7,
									midqualified_per_item: 0.79,
									nonqualified_rate: 2.2,
									nonqualified_per_item: 0.79,
								}
							}
							sameAsAbove={sameAsAbove}
							onSameAsAboveChange={handleSameAsAboveChange}
							onDebitRateChange={(tier, value) => {
								// Direct update of debit rates
								const updatedDebitRates = {
									qualified_rate: 1.6,
									qualified_per_item: 0.79,
									midqualified_rate: 1.7,
									midqualified_per_item: 0.79,
									nonqualified_rate: 2.2,
									nonqualified_per_item: 0.79,
									...configuration.debit_rates,
									[tier]: value,
								};

								onUpdate({ debit_rates: updatedDebitRates });
							}}
						/>
					</>
				) : (
					<>
						{/* Card-Specific Pricing */}
						<CardSpecificSection
							threeTierRates={threeTierRates}
							sixTierRates={sixTierRates}
							sameAsAbove={sameAsAbove}
							onThreeTierChange={(cardType, tier, value) => {
								if (cardType === 'visa_mastercard_discover_credit') {
									// Update credit rates for Visa/Mastercard/Discover
									const updatedCreditRates = {
										...configuration.credit_rates,
										[tier]: value,
									} as TieredRateStructure;
									onUpdate({ credit_rates: updatedCreditRates });
								} else if (cardType === 'american_express') {
									// Update separate AMEX rates
									const updatedAmexRates = {
										qualified_rate: 1.85,
										qualified_per_item: 0.1,
										midqualified_rate: 2.5,
										midqualified_per_item: 0.1,
										nonqualified_rate: 2.85,
										nonqualified_per_item: 0.1,
										...configuration.amex_rates,
										[tier]: value,
									} as TieredRateStructure;
									onUpdate({ amex_rates: updatedAmexRates });
								}
							}}
							onSixTierChange={(cardType, tier, value) => {
								// If "Same as above" is checked, uncheck it when user manually changes 6-tier rates
								const shouldUncheckSameAsAbove = sameAsAbove;
								if (shouldUncheckSameAsAbove) {
									setSameAsAbove(false);
								}

								if (cardType === 'visa_mastercard_discover_credit') {
									// Update debit rates for Visa/Mastercard/Discover Credit (with_debit)
									const defaultDebitRates = {
										qualified_rate: 1.6,
										qualified_per_item: 0.79,
										midqualified_rate: 1.7,
										midqualified_per_item: 0.79,
										nonqualified_rate: 2.2,
										nonqualified_per_item: 0.79,
									};
									const updatedDebitRates = {
										...defaultDebitRates,
										...configuration.debit_rates,
										[tier]: value,
									};
									onUpdate({
										debit_rates: updatedDebitRates,
										...(shouldUncheckSameAsAbove && {
											same_as_credit_6tier: false,
										}),
									});
								} else if (cardType === 'visa_mastercard_discover_debit') {
									// Update separate Visa debit rates
									const updatedVisaDebitRates = {
										qualified_rate: 0.55,
										qualified_per_item: 0.15,
										midqualified_rate: 1.65,
										midqualified_per_item: 0.15,
										nonqualified_rate: 2.1,
										nonqualified_per_item: 0.15,
										...configuration.visa_debit_rates,
										[tier]: value,
									};
									onUpdate({
										visa_debit_rates: updatedVisaDebitRates,
										...(shouldUncheckSameAsAbove && {
											same_as_credit_6tier: false,
										}),
									});
								} else if (cardType === 'pin_debit') {
									// Update separate PIN debit rates
									const updatedPinDebitRates = {
										qualified_rate: 1.85,
										qualified_per_item: 0.1,
										midqualified_rate: 2.5,
										midqualified_per_item: 0.1,
										nonqualified_rate: 2.85,
										nonqualified_per_item: 0.1,
										...configuration.pin_debit_rates,
										[tier]: value,
									};
									onUpdate({
										pin_debit_rates: updatedPinDebitRates,
										...(shouldUncheckSameAsAbove && {
											same_as_credit_6tier: false,
										}),
									});
								}
							}}
							onSameAsAboveChange={handleSameAsAboveChange}
						/>
					</>
				)}
			</div>
		</>
	);
};
